import Papa from 'papaparse'

import type { ItemDto } from '@b-stock/listing-api-client'

import { getUserMinBidAmount } from '@/helpers/getUserMinBidAmount'
import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

/**
 * The exact header text for each column. listingId/bidSubmission must match
 * the header names `parseBidSheetCsv` looks for on upload
 * (Listings.ListView.header.*) — this sheet is itself a valid upload once the
 * buyer fills in Bid Submission.
 */
type DownloadableBidSheetHeaders = {
  title: string
  listingId: string
  minRequiredBid: string
  bidSubmission: string
  perUnitBid: string
  model: string
  grade: string
  carrier: string
  capacity: string
  quantity: string
  url: string
}

const baseUrl =
  typeof window !== 'undefined' ? window.location.origin : 'https://bstock.com'

// Mirrors the "url" column's valueGetter in useColumnDefs.ts — same field,
// same relative-vs-absolute handling.
const getListingUrl = (listing: CombinedListingSearchResult): string => {
  const { auctionUrl } = listing
  if (!auctionUrl) return ''
  return auctionUrl.slice(0, 4) === 'http'
    ? auctionUrl
    : `${baseUrl}${auctionUrl}`
}

// Prefers the mobile-specific attributes.item.* fields (populated only for
// mobile device inventory — see the order-process API's own doc comment on
// GetManifestItemsByLotIdSortByEnum) over the general attributes.* fields,
// since every item reaching this function already belongs to a
// mobile-category storefront lot (see fetchMobileManifestItems).
// Ingestion only fills item.grade from a manifest's own "Grade" column —
// absent on most mobile-device manifests, which carry the same concept at
// item.mobileDevice.sellerGrade instead (the field order-process's own
// manifest export and the listing page's Full Manifest table already read).
const getManifestLineFields = (item: ItemDto) => ({
  model: item.attributes.item?.model ?? item.attributes.model ?? '',
  carrier: item.attributes.item?.carrier ?? item.attributes.carrier ?? '',
  capacity: item.attributes.item?.capacity ?? item.attributes.capacity ?? '',
  grade: item.grade ?? item.mobileDevice?.sellerGrade ?? '',
  quantity: item.quantity !== undefined ? String(item.quantity) : '',
})

const EMPTY_MANIFEST_ITEMS = new Map<string, ItemDto[]>()
const EMPTY_MOBILE_LISTING_IDS: ReadonlySet<string> = new Set()

/**
 * Builds the downloadable "Upload Bulk Bids" sheet.
 *
 * For a listing with no manifest data (the common case, and every listing on
 * a non-mobile-category storefront): one row with its title and minimum
 * required bid for reference, and a blank Bid Submission column.
 *
 * For a listing with manifest line items (mobile-category storefronts only —
 * see `fetchMobileManifestItems`): a lot row (title, minimum required bid,
 * and its own blank Bid Submission for a single lot-level bid), followed by
 * one row per manifest line (model, grade, carrier, capacity, quantity, URL,
 * and its own Per-Unit Bid column). Line rows repeat the lot's Listing ID —
 * upload parsing groups rows by Listing ID and sums whatever per-unit bids
 * come back into that lot's total, so the grouping needs a shared key. A
 * lot-level Bid Submission, if also filled in, takes precedence over the
 * summed per-unit total for that same lot (surfaced on the review screen)
 * rather than the two being combined.
 *
 * `escapeFormulae: true` neutralizes spreadsheet formulas in seller-supplied
 * text (listing title, manifest model/carrier/capacity/grade) by prefixing a
 * leading `'` — this sheet is explicitly meant to be opened in Excel/Sheets,
 * so an unescaped `=`/`+`/`-`/`@`-leading cell would otherwise execute as a
 * live formula on the buyer's machine.
 */
export const buildDownloadableBidSheetCsv = (
  listings: CombinedListingSearchResult[],
  headers: DownloadableBidSheetHeaders,
  manifestItemsByListingId: Map<string, ItemDto[]> = EMPTY_MANIFEST_ITEMS,
  mobileListingIds: ReadonlySet<string> = EMPTY_MOBILE_LISTING_IDS
): string => {
  const fields = [
    headers.title,
    headers.listingId,
    headers.minRequiredBid,
    headers.model,
    headers.grade,
    headers.carrier,
    headers.capacity,
    headers.quantity,
    headers.url,
    headers.bidSubmission,
    headers.perUnitBid,
  ]

  const data: string[][] = []

  for (const listing of listings) {
    // Prefer the device model over the general title, but ONLY on a
    // mobile-category-storefront lot — model is a generic, category-
    // unrestricted search attribute, so a non-mobile listing can carry one
    // too, and substituting it there would replace the title the buyer saw
    // in the list view with text they never saw. Uses mobileListingIds (a
    // direct storefront-based classification, see fetchMobileListingIds)
    // rather than manifestItemsByListingId's own keys — that map omits a
    // genuinely mobile lot with no lotId, or any lot past
    // MAX_MOBILE_MANIFEST_LISTINGS, which would otherwise read as non-mobile
    // here while validateBidSheetRows.ts's review screen (driven by the same
    // mobileListingIds) still read it as mobile.
    const title =
      mobileListingIds.has(listing.listingId) &&
      listing.model &&
      listing.model.length > 0
        ? listing.model.join(', ')
        : listing.title
    const minRequiredBid = String(
      getUserMinBidAmount({
        bidAmount: listing.bidAmount,
        bidPrecision: listing.bidPrecision,
        nextMinBidAmount: listing.nextMinBidAmount,
        startPrice: listing.startPrice,
        winningBidAmount: listing.winningBidAmount,
      })
    )

    // The lot row never carries manifest-line fields — those describe an
    // individual line, not the lot as a whole.
    data.push([
      title,
      listing.listingId,
      minRequiredBid,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ])

    const items = manifestItemsByListingId.get(listing.listingId)
    if (!items?.length) continue

    const url = getListingUrl(listing)
    for (const item of items) {
      const line = getManifestLineFields(item)
      // Title and Minimum Required Bid stay blank — those are read from the
      // live listing lookup on upload, never from this sheet's own columns,
      // so repeating them here would just be noise. Listing ID IS repeated
      // (see the doc comment above) so upload parsing can group this line
      // back to its lot.
      data.push([
        '',
        listing.listingId,
        '',
        line.model,
        line.grade,
        line.carrier,
        line.capacity,
        line.quantity,
        url,
        '',
        '',
      ])
    }
  }

  return Papa.unparse({ fields, data }, { escapeFormulae: true })
}
